import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationListResponse } from '@/features/notifications/types';
import { apiFetch } from '@/lib/api';

/**
 * Notifications — TanStack Query hooks.
 *
 * Endpoints covered (all under /api/v1/notifications):
 *   GET    /notifications?unread&limit&before       list (paginated by createdAt DESC)
 *   PATCH  /notifications/:id/read                  mark one read
 *   PATCH  /notifications/read-all                  bulk mark read (notifications.view perm)
 *
 * SSE delivers the live feed (see `lib/sse-manager.ts`). The list
 * hook is the cold-start + fallback for the bell dropdown — when the
 * SSE socket is connected, the manager pushes updates into the same
 * `useNotifications` cache via `qc.setQueryData`.
 */

export interface NotificationsListParams {
  unreadOnly?: boolean;
  limit?: number;
}

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (params: NotificationsListParams) => ['notifications', 'list', params] as const,
  unread: ['notifications', 'unread'] as const,
};

export function useNotifications(params: NotificationsListParams = {}) {
  const sp = new URLSearchParams();
  sp.set('unread', params.unreadOnly ? 'true' : 'false');
  sp.set('limit', String(params.limit ?? 50));
  return useQuery({
    queryKey: notificationsKeys.list(params),
    queryFn: () => apiFetch<NotificationListResponse>(`/notifications?${sp.toString()}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true; updated: number }>(`/notifications/${id}/read`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}

export function useMarkAllAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true; updatedCount: number }>('/notifications/read-all', {
        method: 'PATCH',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
}
